import type { CalculateOffersResponse, Domain, OfferItem } from '@/services/inventory-api.service';
import { type CalculateOffersPerson } from '@/services/inventory-api.service';
import {
  buildAgeMapFromRestrictions,
  buildBirthDateMapFromRestrictions,
  buildPeopleFromSkierCounts,
} from '@/utils/calculate-offers-people';
import type { CheckoutPassSelectionDto } from '@/services/orders.service';
import type { BookingSkierFormData } from '@/store/book-ski-pass.store';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import { isValidBirthDate } from '@/utils/booking';
import { fetchDomainOffersEntries } from '@/utils/fetch-domain-offers-entries';
import { buildSkierCountOffersFetchKey } from '@/utils/offers-fetch-key';
import { takeSkierCountOffersPrefetchIfKeyMatches } from '@/utils/skier-count-offers-prefetch';
import { expandPassSelections, expandSelectedOffers } from '@/utils/skier-slots';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type OffersResult = {
  items: OfferItem[];
  total: number;
  currency: string;
  people?: CalculateOffersResponse['people'];
};

export type DomainOffersEntry = {
  domain: Domain;
  offers: OffersResult;
};

function getAgeForCategory(ageMap: Record<string, number>, ageCategory: string): number {
  const key = Object.keys(ageMap).find((k) => k.toLowerCase() === ageCategory.toLowerCase());
  return key ? ageMap[key] ?? 30 : 30;
}

function getBirthDateForCategory(
  birthDateMap: Record<string, string>,
  ageCategory: string
): string | undefined {
  const key = Object.keys(birthDateMap).find((k) => k.toLowerCase() === ageCategory.toLowerCase());
  return key ? birthDateMap[key] : undefined;
}

function buildPeopleFromPassSelections(
  passSelections: CheckoutPassSelectionDto[],
  startDate: string,
  days: number,
  ageMap: Record<string, number>,
  birthDateMap: Record<string, string>,
  skierForms: BookingSkierFormData[]
): CalculateOffersPerson[] {
  const slots = expandPassSelections(passSelections);
  if (slots.length === 0) return [];

  const people: CalculateOffersPerson[] = [];
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot) continue;
    const birthDate = skierForms[i]?.birthDate?.trim();
    if (birthDate && isValidBirthDate(birthDate)) {
      people.push({ startDate, days, birthDate, domain_id: slot.domain.id });
      continue;
    }
    const categoryBirthDate = getBirthDateForCategory(birthDateMap, slot.ageCategory);
    if (categoryBirthDate) {
      people.push({ startDate, days, birthDate: categoryBirthDate, domain_id: slot.domain.id });
    } else {
      people.push({
        startDate,
        days,
        age: getAgeForCategory(ageMap, slot.ageCategory),
        domain_id: slot.domain.id,
      });
    }
  }
  return people;
}

function buildPeopleFromSlotsAndForms(
  dataByDomain: DomainOffersEntry[],
  selectedPassQuantities: Record<string, number>,
  startDate: string,
  days: number,
  ageMap: Record<string, number>,
  birthDateMap: Record<string, string>,
  skierForms: BookingSkierFormData[]
): CalculateOffersPerson[] {
  const slots = expandSelectedOffers(dataByDomain, selectedPassQuantities);
  if (slots.length === 0) return [];

  const prevPeople = dataByDomain[0]?.offers?.people ?? [];

  const people: CalculateOffersPerson[] = [];
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot) continue;
    const birthDate = skierForms[i]?.birthDate?.trim();
    if (birthDate && isValidBirthDate(birthDate)) {
      people.push({ startDate, days, birthDate, domain_id: slot.domain.id });
      continue;
    }
    const prevCategory =
      prevPeople[i]?.items?.[0]?.customer_type ?? (prevPeople[i] ? 'Adult' : undefined);
    const category = prevCategory ?? slot.ageCategory;
    const categoryBirthDate = getBirthDateForCategory(birthDateMap, category);
    if (categoryBirthDate) {
      people.push({ startDate, days, birthDate: categoryBirthDate, domain_id: slot.domain.id });
    } else {
      people.push({
        startDate,
        days,
        age: getAgeForCategory(ageMap, category),
        domain_id: slot.domain.id,
      });
    }
  }
  return people;
}

export type UseCalculateOffersOptions = {
  passSelections?: CheckoutPassSelectionDto[];
  skipPersonBirthDates?: boolean;
};

export function useCalculateOffers(options: UseCalculateOffersOptions = {}): {
  dataByDomain: DomainOffersEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const { passSelections, skipPersonBirthDates = false } = options;
  const selectedDomain = useBookSkiPassStore((state) => state.selectedDomain);
  const referenceResortDomains = useBookSkiPassStore((state) => state.referenceResortDomains);
  const stepTwoFormFieldsValues = useBookSkiPassStore((state) => state.stepTwoFormFieldsValues);
  const selectedPassQuantities = useBookSkiPassStore((state) => state.selectedPassQuantities);
  const skierForms = useBookSkiPassStore((state) => state.stepThreeFormFieldsValues.skierForms);

  const [dataByDomain, setDataByDomain] = useState<DomainOffersEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string | null>(null);
  const waitingForDomainsRef = useRef(false);
  const dataByDomainRef = useRef<DomainOffersEntry[]>([]);
  const skierFormsRef = useRef(skierForms);

  useEffect(() => {
    dataByDomainRef.current = dataByDomain;
  }, [dataByDomain]);

  skierFormsRef.current = skierForms;

  const birthDatesKey = useMemo(
    () => (skipPersonBirthDates ? '' : JSON.stringify(skierForms.map((f) => f.birthDate))),
    [skierForms, skipPersonBirthDates]
  );

  const fetchOffers = useCallback(async () => {
    const domainIds = referenceResortDomains?.map((d) => d.id) ?? [];
    const selectedDomainId = selectedDomain?.pk;
    const shouldAddSelectedDomain =
      selectedDomainId != null && !domainIds.includes(selectedDomainId);
    const domainsToFetch =
      domainIds.length > 0
        ? shouldAddSelectedDomain
          ? [selectedDomainId, ...domainIds]
          : domainIds
        : selectedDomainId != null
          ? [selectedDomainId]
          : [];

    if (domainsToFetch.length === 0) {
      waitingForDomainsRef.current = true;
      setError(null);
      setDataByDomain([]);
      lastFetchKeyRef.current = null;
      return;
    }
    waitingForDomainsRef.current = false;

    const days = parseInt(stepTwoFormFieldsValues.daysOfSkiing, 10);
    const startDate = stepTwoFormFieldsValues.startingDate;
    if (!startDate || Number.isNaN(days) || days < 1) {
      setError('Invalid trip details');
      return;
    }

    const ageMap = buildAgeMapFromRestrictions(selectedDomain?.restrictions);
    const birthDateMap = buildBirthDateMapFromRestrictions(
      selectedDomain?.restrictions,
      startDate
    );

    const forms = skipPersonBirthDates ? [] : skierFormsRef.current;
    const passSelectionsPeople =
      passSelections && passSelections.length > 0
        ? buildPeopleFromPassSelections(
            passSelections,
            startDate,
            days,
            ageMap,
            birthDateMap,
            forms
          )
        : [];

    const hasSlotSelections = Object.values(selectedPassQuantities).some((q) => q > 0);
    const slotBasedPeople =
      passSelectionsPeople.length === 0 &&
      hasSlotSelections &&
      dataByDomainRef.current.length > 0 &&
      forms.length > 0
        ? buildPeopleFromSlotsAndForms(
            dataByDomainRef.current,
            selectedPassQuantities,
            startDate,
            days,
            ageMap,
            birthDateMap,
            forms
          )
        : [];

    const people =
      passSelectionsPeople.length > 0
        ? passSelectionsPeople
        : slotBasedPeople.length > 0
          ? slotBasedPeople
          : buildPeopleFromSkierCounts(
              stepTwoFormFieldsValues.skierCounts,
              startDate,
              days,
              ageMap,
              birthDateMap
            );

    if (people.length === 0) {
      setError('No participants');
      return;
    }

    const fetchKey = buildSkierCountOffersFetchKey(
      domainsToFetch,
      stepTwoFormFieldsValues,
      people
    );
    if (lastFetchKeyRef.current === fetchKey) return;

    const prefetched = takeSkierCountOffersPrefetchIfKeyMatches(fetchKey);
    if (prefetched) {
      lastFetchKeyRef.current = fetchKey;
      setDataByDomain(prefetched);
      setError(null);
      return;
    }

    lastFetchKeyRef.current = fetchKey;

    setLoading(true);
    setError(null);
    try {
      const results = await fetchDomainOffersEntries(
        domainsToFetch,
        people,
        selectedDomain ?? undefined
      );

      setDataByDomain(results);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to calculate offers';
      setError(message);
      setDataByDomain([]);
      lastFetchKeyRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [
    selectedDomain,
    referenceResortDomains,
    stepTwoFormFieldsValues,
    selectedPassQuantities,
    birthDatesKey,
    passSelections,
    skipPersonBirthDates,
  ]);

  useEffect(() => {
    void fetchOffers();
  }, [fetchOffers]);

  useEffect(() => {
    if (!waitingForDomainsRef.current) return;
    const domainIds = referenceResortDomains?.map((d) => d.id) ?? [];
    const domainsToFetch =
      domainIds.length > 0 ? domainIds : selectedDomain ? [selectedDomain.pk] : [];
    if (domainsToFetch.length === 0) return;
    void fetchOffers();
  }, [fetchOffers, referenceResortDomains, selectedDomain]);

  const refetch = useCallback(() => {
    lastFetchKeyRef.current = null;
    void fetchOffers();
  }, [fetchOffers]);

  return { dataByDomain, loading, error, refetch };
}
